import { Box, LinearProgress, Paper } from "@mui/material";
import Typography from "components/basic/typography";
import { AnalyticsFunnelQuery } from "generated/graphql";
import { SERIES_COLORS } from "../../constants";
import { formatNumber, formatPercent, formatStep } from "../../helpers/format";

interface EstimatorFunnelProps {
  steps: AnalyticsFunnelQuery["analyticsFunnel"];
  isLoading: boolean;
}

/**
 * Where visitors leave the charger estimator.
 *
 * Bars rather than a line: the steps are named categories, not a continuum. One
 * series, so no legend — every bar is directly labelled with its count and its
 * share of the people who started.
 */
const EstimatorFunnel: React.FC<EstimatorFunnelProps> = ({
  steps,
  isLoading,
}) => {
  const entry = steps[0]?.visitors ?? 0;

  return (
    <Paper variant="outlined" sx={{ padding: 2, borderRadius: 2 }}>
      <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
        Charger estimator funnel
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        Visitors reaching each step, as a share of those who started.
      </Typography>

      <Box sx={{ marginTop: 2 }}>
        {isLoading && <LinearProgress />}

        {!isLoading && steps.length === 0 && (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Nobody has opened the estimator in this range.
          </Typography>
        )}

        {steps.map((step) => (
          <Box key={step.step} sx={{ marginBottom: 1.5 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 2,
                marginBottom: 0.5,
              }}
            >
              <Typography variant="body2">{formatStep(step.step)}</Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {formatNumber(step.visitors)} ·{" "}
                {formatPercent(step.visitors, entry)}
              </Typography>
            </Box>

            <Box
              sx={{
                height: 10,
                borderRadius: "5px",
                backgroundColor: "action.hover",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  borderRadius: "5px",
                  width: entry > 0 ? `${(step.visitors / entry) * 100}%` : 0,
                  backgroundColor: SERIES_COLORS.pageviews,
                }}
              />
            </Box>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

export default EstimatorFunnel;
